import { isNil } from 'lodash-es';
import React, { FC, useCallback } from 'react';
import { DetailsCard, TextTransformEnum } from '@getvim/atomic-ui';
import { formatDateString } from '@getvim/react-app-infra';
import { GetMemberResult } from '../../../types';
import { CoPayColumn, EligibilityCardItem } from '../../molecules';
import { ClickAction, DetailsCardType, Feature } from '../../../utils/analytics';
import './index.less';
import { analyticsManager } from '../../../utils/analyticManager';
import { useFeatureFlag } from '../../../utils/featureFlags';

export interface MemberDetailsListProps {
  eligibility: GetMemberResult;
}

const formatCoPayStr = (copay?: number, frequency?: string) =>
  // Example: For copay of 20 and frequency '/month' => '$20 / month'
  // .replace(/^\//, '') removes the leading slash from the frequency, if present
  !isNil(copay)
    ? `$${copay}${!isNil(frequency) ? ` / ${frequency.replace(/^\//, '')}` : ''}`
    : 'Not available';

export const MemberDetailsList: FC<MemberDetailsListProps> = ({ eligibility }) => {
  const {
    memberName,
    dateOfBirth,
    memberId,
    groupNumber,
    plan,
    coverage,
    primaryProvider,
    referral,
    coPay,
    coPayStructured,
  } = eligibility;

  const [shouldUseStructuredCoPay, isStructuredCoPayFlagLoading] = useFeatureFlag({
    flagName: 'shouldUseStructuredCoPay',
    defaultValue: false,
  });
  const handleToggle = useCallback(
    (expanded: boolean) => {
      analyticsManager.track('vim_chart_item_click', {
        feature: Feature.Eligibility,
        action: expanded ? ClickAction.Open : ClickAction.Close,
        detailsCardType: DetailsCardType.MemberDetails,
        app_source_displayed: eligibility?.dataSource,
      });
    },
    [eligibility?.dataSource],
  );

  let referralValue;
  const pcpCopay = coPay?.pcp?.inNetwork;
  const pcpCopayFrequency = coPay?.pcp?.frequency;
  const specialistCopay = coPay?.specialist?.inNetwork;
  const specialistCopayFrequency = coPay?.specialist?.frequency;

  const hasStructuredCoPayData =
    (coPayStructured?.pcp?.response?.length ?? 0) > 0 ||
    (coPayStructured?.specialist?.response?.length ?? 0) > 0;
  const hasLegacyCoPayData = !isNil(pcpCopay) || !isNil(specialistCopay);

  const renderCoPayRow = () => {
    if (isStructuredCoPayFlagLoading) return null;

    if (shouldUseStructuredCoPay && coPayStructured) {
      if (!hasStructuredCoPayData) return null;
      return (
        <div className="copay-row">
          <CoPayColumn
            label="Primary care co-pay"
            detail={coPayStructured.pcp}
            dataSource={eligibility?.dataSource}
          />
          <CoPayColumn
            label="Specialist co-pay"
            detail={coPayStructured.specialist}
            dataSource={eligibility?.dataSource}
          />
        </div>
      );
    }

    if (!hasLegacyCoPayData) return null;
    return (
      <div className="copay-row">
        <EligibilityCardItem
          label="Primary care co-pay"
          value={formatCoPayStr(pcpCopay, pcpCopayFrequency)}
        />
        <EligibilityCardItem
          label="Specialist co-pay"
          value={formatCoPayStr(specialistCopay, specialistCopayFrequency)}
        />
      </div>
    );
  };

  if (!isNil(referral)) {
    referralValue = referral ? 'Required' : 'Not required';
  }

  const renderTopCard = () => {
    return (
      <>
        <div className="row-member-details-list">
          <EligibilityCardItem
            id="name"
            label="Name"
            value={memberName?.toLowerCase()}
            valueTextTransform={TextTransformEnum.capitalize}
          />
          <EligibilityCardItem
            id="dob"
            label={'Date of birth'}
            value={dateOfBirth ? formatDateString(dateOfBirth) : ''}
          />
        </div>
        <div className="row-member-details-list">
          {memberId && <EligibilityCardItem id="memberID" label="Member ID" value={memberId} />}
          {groupNumber && (
            <EligibilityCardItem id="groupNumber" label={'Group number'} value={groupNumber} />
          )}
        </div>
      </>
    );
  };
  return (
    <DetailsCard
      title={'Member & eligibility details'}
      iconClass="icon-clipboard"
      onToggle={handleToggle}
      smallContentPadding
    >
      {renderTopCard()}
      {plan && (
        <EligibilityCardItem
          label="Plan"
          value={plan}
          valueTextTransform={TextTransformEnum.upperCase}
        />
      )}
      {coverage && <EligibilityCardItem label="Coverage" value={coverage} />}
      {primaryProvider && (
        <EligibilityCardItem
          label={'Primary care provider'}
          value={primaryProvider.toLowerCase()}
          valueTextTransform={TextTransformEnum.capitalize}
        />
      )}
      {referralValue && <EligibilityCardItem label="Referral" value={referralValue} />}

      {renderCoPayRow()}
    </DetailsCard>
  );
};
